// client/src/components/inventory/SupplierModal.tsx
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Mail, Phone, MapPin, FileText, Star, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { createSupplier, updateSupplier } from "@/lib/api";

interface SupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  supplier?: any; // Para edición
  mode: 'create' | 'edit';
  onSuccess: () => void;
}

export default function SupplierModal({ 
  open, 
  onOpenChange, 
  shopId, 
  supplier, 
  mode, 
  onSuccess 
}: SupplierModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    contactName: "",
    email: "",
    phone: "",
    address: "",
    taxId: "",
    paymentTerms: "30 días",
    deliveryTime: 7,
    rating: 5,
    notes: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

// Inicializar formulario con datos del proveedor (si existe)
useEffect(() => {
  if (!open) {
    return;
  }
  if (supplier && mode === 'edit') {
    setFormData({
      name: supplier.name || "",
      contactName: supplier.contactName || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      taxId: supplier.taxId || "",
      paymentTerms: supplier.paymentTerms || "30 días",
      deliveryTime: supplier.deliveryTime || 7,
      rating: supplier.rating || 5,
      notes: supplier.notes || ""
    });
  } else if (mode === 'create') {
    setFormData({
      name: "",
      contactName: "",
      email: "",
      phone: "",
      address: "",
      taxId: "",
      paymentTerms: "30 días",
      deliveryTime: 7,
      rating: 5,
      notes: ""
    });
  }
  setError(null);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);
  setError(null);

  try {
    // Preparar datos con el tipo exacto que espera createSupplier
    const supplierData = {
      shopId,
      name: formData.name.trim(),
      contactName: formData.contactName.trim() || undefined,
      email: formData.email.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      address: formData.address.trim() || undefined,
      taxId: formData.taxId.trim() || undefined,
      paymentTerms: formData.paymentTerms,
      deliveryTime: formData.deliveryTime,
      rating: formData.rating,
      notes: formData.notes.trim() || undefined
    };

    // Filtrar campos undefined (opcionales)
    const filteredData: any = {};
    Object.keys(supplierData).forEach(key => {
      const value = (supplierData as any)[key];
      if (value !== undefined && value !== '') {
        filteredData[key] = value;
      }
    });

    if (mode === 'create') {
      // Crear proveedor usando la API
      const result = await createSupplier(filteredData);
      
      if (result.success) {
        toast({
          title: "¡Proveedor creado!",
          description: result.message || `"${result.supplier?.name}" ha sido creado exitosamente.`,
        });
        
        onSuccess();
        onOpenChange(false);
      } else {
        // Si success es false (aunque la API debería lanzar error antes)
        throw new Error(result.message || "Error al crear el proveedor");
      }
      
    } else if (supplier && mode === 'edit') {
      // Actualizar proveedor usando la API
      const result = await updateSupplier(supplier.id, filteredData);
      
      if (result.success) {
        toast({
          title: "¡Proveedor actualizado!",
          description: result.message || `"${result.supplier?.name}" ha sido actualizado exitosamente.`,
        });
        
        onSuccess();
        onOpenChange(false);
      } else {
        throw new Error(result.message || "Error al actualizar el proveedor");
      }
    }
    
  } catch (error: any) {
    console.error("Error guardando proveedor:", error);
    const errorMessage = error.message || "Error al guardar el proveedor";
    setError(errorMessage);
    
    // Mostrar toast de error
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive"
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Truck className="h-5 w-5 mr-2" />
            {mode === 'create' ? 'Agregar Nuevo Proveedor' : 'Editar Proveedor'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Completa la información del nuevo proveedor'
              : 'Actualiza la información del proveedor'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {error && (
            <div className="bg-red-50 text-red-800 p-3 rounded text-sm">
              ❌ {error}
            </div>
          )}

          {/* Información básica */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Información Básica</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center">
                  <Truck className="h-3 w-3 mr-1" />
                  Nombre del Proveedor *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Distribuidora ABC"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactName" className="flex items-center">
                  <FileText className="h-3 w-3 mr-1" />
                  Persona de Contacto
                </Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  placeholder="Ej: Juan Pérez"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center">
                  <Mail className="h-3 w-3 mr-1" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="proveedor@ejemplo.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center">
                  <Phone className="h-3 w-3 mr-1" />
                  Teléfono
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                Dirección
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Calle, Ciudad, Estado"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxId">
                Identificación Fiscal (RUC/NIT/RFC)
              </Label>
              <Input
                id="taxId"
                value={formData.taxId}
                onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                placeholder="Ej: 12345678901"
              />
            </div>
          </div>

          {/* Términos y evaluación */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Términos y Evaluación</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paymentTerms" className="flex items-center">
                  <FileText className="h-3 w-3 mr-1" />
                  Términos de Pago
                </Label>
                <Select
                  value={formData.paymentTerms}
                  onValueChange={(value) => setFormData({...formData, paymentTerms: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Contado">Contado</SelectItem>
                    <SelectItem value="7 días">7 días</SelectItem>
                    <SelectItem value="15 días">15 días</SelectItem>
                    <SelectItem value="30 días">30 días</SelectItem>
                    <SelectItem value="60 días">60 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryTime" className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" />
                  Tiempo de Entrega (días)
                </Label>
                <Input
                  id="deliveryTime"
                  type="number"
                  min="1"
                  value={formData.deliveryTime}
                  onChange={(e) => setFormData({...formData, deliveryTime: parseInt(e.target.value) || 7})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rating" className="flex items-center">
                <Star className="h-3 w-3 mr-1" />
                Calificación (1-5 estrellas)
              </Label>
              <Select
                value={formData.rating.toString()}
                onValueChange={(value) => setFormData({...formData, rating: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar calificación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">⭐ (1) - Muy malo</SelectItem>
                  <SelectItem value="2">⭐⭐ (2) - Malo</SelectItem>
                  <SelectItem value="3">⭐⭐⭐ (3) - Regular</SelectItem>
                  <SelectItem value="4">⭐⭐⭐⭐ (4) - Bueno</SelectItem>
                  <SelectItem value="5">⭐⭐⭐⭐⭐ (5) - Excelente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notas Adicionales</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Información adicional sobre el proveedor..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Guardando..." : mode === 'create' ? "Crear Proveedor" : "Actualizar Proveedor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}